import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  type AutomationHookKind,
  type WorkspaceAutomationHook,
  deleteAutomationHook,
  listAutomationHooks,
  testAutomationHook,
  upsertAutomationHook,
} from "@/lib/workspaceAutomation";

type Props = { workspaceId: string };

type RowProps = {
  kind: AutomationHookKind;
  title: string;
  description: string;
  placeholder: string;
  helpUrl: string;
  workspaceId: string;
  initial: WorkspaceAutomationHook | null;
  onChanged: () => void;
};

const isHttps = (v: string) => {
  try {
    return new URL(v.trim()).protocol === "https:";
  } catch {
    return false;
  }
};

const HookRow = ({ kind, title, description, placeholder, helpUrl, workspaceId, initial, onChanged }: RowProps) => {
  const [url, setUrl] = useState(initial?.webhook_url ?? "");
  const [enabled, setEnabled] = useState(initial?.enabled ?? true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    setUrl(initial?.webhook_url ?? "");
    setEnabled(initial?.enabled ?? true);
  }, [initial?.webhook_url, initial?.enabled]);

  const dirty = (initial?.webhook_url ?? "") !== url.trim() || (initial?.enabled ?? true) !== enabled;

  const onSave = async () => {
    const v = url.trim();
    if (!isHttps(v)) {
      toast.error("Webhook URL must be a valid HTTPS URL");
      return;
    }
    setSaving(true);
    try {
      await upsertAutomationHook(workspaceId, kind, v, enabled);
      toast.success(`${title} saved`);
      onChanged();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    if (!initial) {
      toast.error("Save the URL first, then test.");
      return;
    }
    setTesting(true);
    try {
      const r = await testAutomationHook(workspaceId, kind);
      if (r.ok) toast.success(`Test event sent to ${title} (HTTP ${r.http_status})`);
      else toast.error(`Test failed: ${r.error ?? `HTTP ${r.http_status ?? "?"}`}`);
      onChanged();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setTesting(false);
    }
  };

  const onRemove = async () => {
    if (!initial) return;
    if (!confirm(`Remove the ${title} webhook? Future events will stop firing.`)) return;
    setRemoving(true);
    try {
      await deleteAutomationHook(workspaceId, kind);
      toast.success(`${title} removed`);
      onChanged();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <div className="space-y-3 rounded-md border p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold">{title}</h4>
            {initial?.last_status === "succeeded" && (
              <Badge variant="secondary" className="text-xs">Last delivery: ok</Badge>
            )}
            {initial?.last_status === "failed" && (
              <Badge variant="destructive" className="text-xs">Last delivery: failed</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Label htmlFor={`hook-enabled-${kind}`} className="text-xs">Enabled</Label>
          <Switch id={`hook-enabled-${kind}`} checked={enabled} onCheckedChange={setEnabled} />
        </div>
      </div>

      <div>
        <Label htmlFor={`hook-url-${kind}`} className="text-xs">Webhook URL</Label>
        <Input
          id={`hook-url-${kind}`}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
        />
      </div>

      {initial?.last_status === "failed" && initial.last_error_message && (
        <p className="text-xs text-destructive">{initial.last_error_message}</p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={onSave} disabled={saving || !dirty || !url.trim()}>
          {saving ? "Saving…" : initial ? "Save" : "Add hook"}
        </Button>
        <Button size="sm" variant="outline" onClick={onTest} disabled={testing || !initial}>
          {testing ? "Testing…" : "Send test"}
        </Button>
        {initial && (
          <Button size="sm" variant="ghost" onClick={onRemove} disabled={removing}>
            {removing ? "Removing…" : "Remove"}
          </Button>
        )}
        <a
          href={helpUrl}
          target="_blank"
          rel="noreferrer"
          className="ml-auto text-xs text-muted-foreground hover:text-foreground underline self-center"
        >
          How to get a webhook URL
        </a>
      </div>
    </div>
  );
};

/**
 * Workspace-level auto-fire webhook hooks for n8n and Zapier.
 *
 * Available on every tier. Fires on `piece.published` immediately after a
 * successful Composer publish dispatch. Treat the webhook URL as a secret —
 * anyone who has it can post events to the user's workflow. Unsigned by
 * design; HMAC signing is a v2 follow-up.
 */
export const AutomationHooksCard = ({ workspaceId }: Props) => {
  const [hooks, setHooks] = useState<WorkspaceAutomationHook[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useMemo(
    () => async () => {
      try {
        const rows = await listAutomationHooks(workspaceId);
        setHooks(rows);
      } catch (e) {
        toast.error((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [workspaceId],
  );

  useEffect(() => {
    setLoading(true);
    void refresh();
  }, [refresh]);

  const n8n = hooks.find((h) => h.kind === "n8n") ?? null;
  const zap = hooks.find((h) => h.kind === "zapier") ?? null;

  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle>Automation hooks</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <p className="text-muted-foreground">
          Fire a webhook to n8n or Zapier whenever a Composer piece is published from this workspace.
          Treat the URL like a password — regenerate it in n8n/Zapier if it leaks.
        </p>
        {loading ? (
          <p className="text-muted-foreground">Loading…</p>
        ) : (
          <>
            <HookRow
              kind="n8n"
              title="n8n"
              description="Paste a Webhook node URL from your n8n workflow."
              placeholder="https://n8n.example.com/webhook/..."
              helpUrl="https://docs.n8n.io/integrations/builtin/core-nodes/n8n-nodes-base.webhook/"
              workspaceId={workspaceId}
              initial={n8n}
              onChanged={refresh}
            />
            <HookRow
              kind="zapier"
              title="Zapier"
              description="Paste a Catch Hook URL from a Webhooks by Zapier trigger."
              placeholder="https://hooks.zapier.com/hooks/catch/..."
              helpUrl="https://zapier.com/apps/webhook/help"
              workspaceId={workspaceId}
              initial={zap}
              onChanged={refresh}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
};
